import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core'
import { Box, CircularProgress, Typography } from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import OutlinedInput from '@mui/material/OutlinedInput'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import { Button } from 'components'
import intl from 'localization/components'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useParams } from 'react-router'
import { useGetGlobalZones } from 'services/queries/useGetGlobalZones'
import { useUpdateCustomer } from 'services/queries/useUpdateCustomer'
import { countries as Countries } from 'utils/constants'

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
  MenuListProps: {
    style: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '1rem',
      padding: '1rem',
      border: '1px solid #ccc',
      borderRadius: '4px',
    },
  },
}

type EditZone = {
  name: string
  countries: string[]
  allocation: number
}

type AddOrEditZoneProps = {
  customerZones: EditZone[]
  data: EditZone | undefined
  open: boolean
  setOpen: Function
  onSubmit: Function
  title: string
}

const AddOrEditZone = ({
  customerZones,
  data,
  open,
  setOpen,
  onSubmit,
  title,
}: AddOrEditZoneProps): JSX.Element => {
  const [isEdit, setIsEdit] = useState<boolean>(false)
  const [selectedZone, setSelectedZone] = useState<string>('')
  const [selectedCountries, setSelectedCountries] = useState<string[]>([])
  const [zones, setZones] = useState<string[]>([])
  const [zoneCountries, setZoneCountries] = useState<string[]>([])
  const [allocation, setAllocation] = useState<number>(0)
  const { id: selectedCustomer } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const reactIntl = useIntl()
  const messages = reactIntl.messages

  const onSuccess = () => {
    const message = isEdit
      ? messages[intl.customerZones('customer-zone-update-success')]
      : messages[intl.customerZones('customer-zone-create-success')]
    enqueueSnackbar(message, {
      variant: 'success',
      autoHideDuration: 3000,
    })
    onSubmit()
    handleClose()
  }

  const onError = () => {
    const message = isEdit
      ? messages[intl.customerZones('customer-zone-update-failed')]
      : messages[intl.customerZones('customer-zone-create-failed')]
    enqueueSnackbar(message, {
      variant: 'error',
      autoHideDuration: 3000,
    })
    onSubmit()
    handleClose()
  }

  const { mutate: updateCustomer, isLoading: isSavingData } = useUpdateCustomer(
    { onSuccess, onError },
  )

  const { data: globalZones, isLoading: isGlobalZonesLoading } =
    useGetGlobalZones()

  const handleZoneChange = (event: SelectChangeEvent<typeof selectedZone>) => {
    const {
      target: { value },
    } = event
    const { data: zonesData } = globalZones

    const allCountries = Array.from(
      new Set(
        zonesData.find((zone: { name: string }) => zone.name === value)
          ?.countries || [],
      ),
    )
    setSelectedZone(value)
    setZoneCountries(allCountries)
    setSelectedCountries([])
  }

  const handleCountriesChange = (event) => {
    const {
      target: { value },
    } = event
    const selectedValues = typeof value === 'string' ? value.split(',') : value
    // Handle "Select All" option
    if (selectedValues.includes('select-all')) {
      // Check if "select-all" was just added to the selection
      if (!selectedCountries.includes('select-all')) {
        // Select all countries
        setSelectedCountries([...zoneCountries])
      } else {
        // Deselect all countries
        setSelectedCountries([])
      }
    } else {
      // Regular selection (without "select-all")
      setSelectedCountries(selectedValues)
    }
  }

  const handleClose = () => {
    setZones([])
    setZoneCountries([])
    setSelectedZone('')
    setSelectedCountries([])
    setAllocation(0)
    setOpen()
    setIsEdit(false)
  }

  const handleSubmit = () => {
    let newAllowZones = customerZones.reduce((acc, zone) => {
      acc[zone.name] = zone.countries
      return acc
    }, {})

    if (newAllowZones[selectedZone]) {
      if (isEdit) {
        newAllowZones[selectedZone] = selectedCountries.map((country) =>
          country.toUpperCase(),
        )
      } else {
        newAllowZones[selectedZone] = [
          ...newAllowZones[selectedZone],
          ...selectedCountries
            .map((country) => country.toUpperCase())
            .filter(
              (country) => !newAllowZones[selectedZone].includes(country),
            ),
        ]
      }
    } else {
      newAllowZones[selectedZone] = selectedCountries.map((country) =>
        country.toUpperCase(),
      )
    }

    const newGlobalLookupLimits = customerZones.reduce((acc, zone) => {
      acc[zone.name] = zone.allocation
      return acc
    }, {})

    if (newGlobalLookupLimits[selectedZone]) {
      if (isEdit) {
        newGlobalLookupLimits[selectedZone] = allocation
      } else {
        newGlobalLookupLimits[selectedZone] += +allocation
      }
    } else {
      newGlobalLookupLimits[selectedZone] = allocation
    }

    const newData = {
      allow_zones: { ...newAllowZones },
      global_lookups_limit: { ...newGlobalLookupLimits },
    }

    updateCustomer({
      companyId: selectedCustomer,
      reqBody: {
        data: {
          ...newData,
        },
      },
    })
  }

  useEffect(() => {
    if (data) {
      const { data: zonesData } = globalZones
      const allCountries = Array.from(
        new Set(
          zonesData.find((zone: { name: string }) => zone.name === data.name)
            ?.countries || [],
        ),
      )

      setIsEdit(true)
      setSelectedZone(data.name)
      setSelectedCountries(
        data.countries?.map((country) => country.toLowerCase()) || [],
      )
      setZoneCountries(allCountries)
      setAllocation(data.allocation)
    } else {
      setIsEdit(false)
    }

    if (globalZones && globalZones.data) {
      const { data: zonesData } = globalZones
      const allzones = zonesData.map((zone) => zone.name)
      setZones(allzones)
    }
  }, [data, globalZones, open])

  const handleCloseDialog = (event, reason) => {
    if (reason && reason === 'backdropClick') {
      return
    }
    handleClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {isGlobalZonesLoading ? (
          <Typography variant="body1">
            <CircularProgress />
            <Typography variant="body1">Loading zones...</Typography>
          </Typography>
        ) : (
          <Box sx={{ display: 'grid', gap: '2rem' }}>
            <FormControl fullWidth disabled={isSavingData || isEdit}>
              <InputLabel id="zone-label">Zone</InputLabel>
              <Select
                labelId="zone-label"
                id="zone-checkbox"
                name="zone-checkbox"
                value={selectedZone}
                onChange={handleZoneChange}
                input={<OutlinedInput label="Zone" />}
                renderValue={(selected) => selectedZone}
                MenuProps={MenuProps}
              >
                {zones.map((name) => (
                  <MenuItem key={name} value={name}>
                    <Checkbox checked={selectedZone.includes(name)} />
                    <ListItemText primary={name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth disabled={isSavingData}>
              <InputLabel id="countries-label">Countries</InputLabel>
              <Select
                labelId="countries-label"
                id="countries-checkbox"
                name="countries-checkbox"
                multiple
                value={selectedCountries}
                onChange={handleCountriesChange}
                input={
                  <OutlinedInput
                    id="select-multiple-countires"
                    label="Countries"
                  />
                }
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selected
                      .filter((value) => !value.includes('select-all'))
                      .map((value) => {
                        const zoneCountry = Countries.getName(
                          value.toUpperCase(),
                          'en',
                        )
                        if (!zoneCountry) {
                          return null
                        }
                        return (
                          <Chip
                            sx={{ fontSize: '1.6rem', fontWeight: 400 }}
                            key={value}
                            label={zoneCountry || value}
                          />
                        )
                      })}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {zoneCountries.length === 0 && (
                  <MenuItem disabled>
                    <ListItemText primary="Select a zone to select countries" />
                  </MenuItem>
                )}
                {zoneCountries.length > 0 && (
                  <MenuItem key="select-all" value={'select-all'}>
                    <ListItemText primary={'Select All'} />
                  </MenuItem>
                )}

                {[...zoneCountries]
                  .sort((a, b) => {
                    const nameA = Countries.getName(a, 'en') || a
                    const nameB = Countries.getName(b, 'en') || b
                    return nameA.localeCompare(nameB)
                  })
                  .map((name) => {
                    const zoneCountry = Countries.getName(name, 'en')
                    return (
                      <MenuItem key={name} value={name}>
                        <Checkbox
                          checked={
                            selectedCountries.includes(name.toLowerCase()) ||
                            selectedCountries.includes(name.toUpperCase())
                          }
                        />
                        <ListItemText primary={zoneCountry || name} />
                      </MenuItem>
                    )
                  })}
              </Select>
            </FormControl>
            <TextField
              id="zone-allocation"
              label="Allocation (no. of lookups)"
              variant="outlined"
              type="number"
              value={allocation}
              disabled={isSavingData}
              onChange={(e) => {
                const value = e.target.value
                if (/^\d*$/.test(value)) {
                  setAllocation(parseInt(value))
                }
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Box
          sx={{ padding: '1rem 2rem 1rem 2rem', display: 'flex', gap: '1rem' }}
        >
          <Button onClick={handleClose} disabled={isSavingData}>
            {<FormattedMessage id={intl.confirmAction('cancel')} />}
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            autoFocus
            disabled={
              isSavingData ||
              !selectedZone ||
              !selectedCountries.length ||
              !allocation
            }
          >
            {isSavingData ? (
              <CircularProgress size={24} />
            ) : (
              <FormattedMessage
                id={
                  isEdit
                    ? intl.confirmAction('update')
                    : intl.confirmAction('add')
                }
              />
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default AddOrEditZone
